const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface WidgetSettings {
  id?: number;
  school_name?: string;
  unique_selling_proposition?: string;
  chatbot_name?: string;
  chatbot_personality?: string;
  primary_color?: string;
  secondary_color?: string;
  background_color?: string;
  text_color?: string;
  accent_color?: string;
  position?: string;
  call_to_action_text?: string;
  button_text?: string;
  avatar_url?: string;
  avatar_filename?: string;
  logo_url?: string;
  logo_filename?: string;
  welcome_message?: string;
  greeting_message?: string;
  show_branding?: boolean;
  enable_typing_indicator?: boolean;
  border_radius?: string;
  enable_analytics?: boolean;
  auto_open_enabled?: boolean;
  auto_open_delay?: number;
  auto_open_only_new_visitors?: boolean;
  newsletter_resource_id?: number | null;
  created_at?: string;
  updated_at?: string;
}

export interface WidgetTheme {
  id: number;
  name: string;
  description?: string;
  color_scheme: {
    primary: string;
    secondary: string;
    background: string;
    text: string;
    accent: string;
  };
  preview_image?: string;
  is_default?: boolean;
  is_premium?: boolean;
  created_at?: string;
}

export interface ImageUploadResponse {
  success: boolean;
  imageUrl: string;
  filename: string;
  type: string;
  message: string;
}

class WidgetSettingsService {
  private getAuthHeaders() {
    const token = localStorage.getItem('chatbot_admin_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  private getFormDataHeaders() {
    const token = localStorage.getItem('chatbot_admin_token');
    return {
      'Authorization': `Bearer ${token}`
      // Note: Don't set Content-Type for FormData, let browser set it with boundary
    };
  }

  async getSettings(): Promise<WidgetSettings> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/widget/settings`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch settings: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching widget settings:', error);
      throw error;
    }
  }

  async updateSettings(settings: Partial<WidgetSettings>): Promise<WidgetSettings> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/widget/settings`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(settings)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to update settings: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating widget settings:', error);
      throw error;
    }
  }

  async uploadImage(file: File, imageType: 'avatar' | 'logo'): Promise<ImageUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('imageType', imageType);

      const response = await fetch(`${API_BASE_URL}/api/widget/upload-image`, {
        method: 'POST',
        headers: this.getFormDataHeaders(),
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to upload ${imageType}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error uploading ${imageType}:`, error);
      throw error;
    }
  }

  async deleteImage(imageType: 'avatar' | 'logo'): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/widget/image/${imageType}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to delete ${imageType}: ${response.statusText}`);
      }
    } catch (error) {
      console.error(`Error deleting ${imageType}:`, error);
      throw error;
    }
  }

  async getThemes(): Promise<WidgetTheme[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/widget/themes`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch themes: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching widget themes:', error);
      throw error;
    }
  }

  async applyTheme(themeId: number): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/widget/themes/${themeId}/apply`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to apply theme: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error applying theme:', error);
      throw error;
    }
  }

  async resetToDefaults(): Promise<WidgetSettings> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/widget/reset-defaults`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to reset settings: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error resetting settings:', error);
      throw error;
    }
  }

  // Utility method to generate widget preview URL
  getWidgetPreviewUrl(): string {
    return process.env.REACT_APP_WIDGET_URL || 'http://localhost:8080';
  }

  // Get full image URL from relative path
  getImageUrl(relativePath: string): string {
    if (!relativePath) return '';

    // If already a full URL, check if it's localhost and we're in production
    if (relativePath.startsWith('http')) {
      // If URL contains localhost but we're not in development, fix it
      if (relativePath.includes('localhost') && !window.location.hostname.includes('localhost')) {
        // Replace localhost URL with current domain
        const filename = relativePath.split('/').pop();
        return `${window.location.origin}/uploads/avatars/${filename}`;
      }
      return relativePath;
    }

    // Otherwise, construct full URL for relative paths
    return `${API_BASE_URL}${relativePath}`;
  }

  // Validate color format
  isValidColor(color: string): boolean {
    return /^#[0-9A-F]{6}$/i.test(color);
  }

  // Generate CSS variables from settings
  generateCSSVariables(settings: WidgetSettings): Record<string, string> {
    return {
      '--primary-color': settings.primary_color || '#4F46E5',
      '--secondary-color': settings.secondary_color || '#7C3AED',
      '--background-color': settings.background_color || '#FFFFFF',
      '--text-color': settings.text_color || '#1F2937',
      '--accent-color': settings.accent_color || '#10B981',
      '--border-radius': settings.border_radius || '12px'
    };
  }

  // Process template variables in messages
  processMessageTemplate(template: string, settings: WidgetSettings): string {
    if (!template) return template;
    
    return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      const variables: Record<string, string> = {
        school_name: settings.school_name || 'My Academy',
        chatbot_name: settings.chatbot_name || 'School Assistant'
      };
      return variables[key] || match;
    });
  }
}

const widgetSettingsService = new WidgetSettingsService();
export default widgetSettingsService;